"use client";

import { useState } from "react";
import {
  User,
  Mail,
  Shield,
  Crown,
  Calendar,
  CreditCard,
  Edit3,
  Check,
  X,
  Phone,
  Building,
  Star,
} from "lucide-react";
import { useSelector } from "react-redux";
import MainLayout from "../../../components/layout/MainLayout";

export default function ProfileIndex() {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const loggedInUser = useSelector((state) => state?.auth?.loggedInUser);
  const [userData] = useState(loggedInUser);

  const getAdminType = (type) => {
    return type === 4 ? "Paying Admin" : "Normal Admin";
  };

  const getAdminBadgeColor = (type) => {
    return type === 4
      ? "bg-gradient-to-r from-purple-500 to-pink-500"
      : "bg-gradient-to-r from-blue-500 to-cyan-500";
  };

  const handleEditName = () => {
    if (isEditing) {
      // Save logic would go here
      console.log("Saving name:", editedName);
      setIsEditing(false);
    } else {
      setEditedName(userData.name);
      setIsEditing(true);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditedName("");
  };

  const formatName = (name) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  return (
    <MainLayout title="User Profile">
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              User Profile
            </h1>
            <p className="text-gray-600">
              Manage your account information and settings
            </p>
          </div>

          {/* Main Profile Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Cover Section */}
            <div className="h-32 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 relative">
              <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            </div>

            {/* Profile Content */}
            <div className="relative px-6 pb-6">
              {/* Profile Image */}
              <div className="flex justify-center -mt-16 mb-6">
                <div className="relative">
                  <img
                    src={userData.image || "/placeholder.svg"}
                    alt="Profile"
                    className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                  />
                  <div className="absolute bottom-2 right-2 w-8 h-8 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>

              {/* Name and Edit */}
              <div className="text-center mb-6">
                {isEditing ? (
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="text-2xl font-bold text-gray-800 bg-gray-50 border border-gray-300 rounded-lg px-3 py-1 text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={handleEditName}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
                      {formatName(userData.name)}
                    </h2>
                    <button
                      onClick={handleEditName}
                      className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {/* Admin Badge */}
                <div className="flex justify-center mb-4">
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold text-sm ${getAdminBadgeColor(
                      userData.type
                    )}`}
                  >
                    {userData.type === 4 ? (
                      <Crown className="w-4 h-4" />
                    ) : (
                      <Shield className="w-4 h-4" />
                    )}
                    {getAdminType(userData.type)}
                  </span>
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                    Personal Information
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-gray-800">
                          {userData.email}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-medium text-gray-800">
                          {userData.phoneNumber || "Not provided"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Building className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Company</p>
                        <p className="font-medium text-gray-800">
                          {userData.company || "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subscription Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                    Subscription Details
                  </h3>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <div>
                        <p className="text-sm text-green-600">Status</p>
                        <p className="font-medium text-green-800">
                          {userData.isSubscription
                            ? "Active Subscription"
                            : "No Subscription"}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">Plan Type</p>
                        <p className="font-medium text-gray-800 capitalize">
                          {userData.planType}ly Plan
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <CreditCard className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-500">
                          Subscription Type
                        </p>
                        <p className="font-medium text-gray-800">
                          Type {userData.subscriptionType}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Features */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">
                  Account Features
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <User className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-blue-800">
                      Self Created
                    </p>
                    <p className="text-xs text-blue-600">
                      {userData.selfCreated ? "Yes" : "No"}
                    </p>
                  </div>

                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <Star className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-purple-800">
                      Special Access
                    </p>
                    <p className="text-xs text-purple-600">
                      {userData.specialAccess ? "Enabled" : "Disabled"}
                    </p>
                  </div>

                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-green-800">
                      Google Auth
                    </p>
                    <p className="text-xs text-green-600">
                      {userData.googleId ? "Connected" : "Not Connected"}
                    </p>
                  </div>

                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <Calendar className="w-8 h-8 text-orange-600 mx-auto mb-2" />
                    <p className="text-sm font-medium text-orange-800">
                      Demo Account
                    </p>
                    <p className="text-xs text-orange-600">
                      {userData.isDemoAccount ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              {/* <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 shadow-lg">
                Edit Profile
              </button>
              <button className="px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
                Account Settings
              </button>
              <button className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg">
                Manage Subscription
              </button>
            </div> */}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
